import React, { useState, useEffect } from 'react';
import { createClient } from '../services/Admin/createClient';
import { updateClient } from '../services/Admin/updateClient';
import { deleteClient } from '../services/Admin/deleteClient';
import { getAllClients } from '../services/Admin/getService';
import Cookies from 'js-cookie';

const ClientsManagement = () => {
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [editingClient, setEditingClient] = useState(null);
    const [newClient, setNewClient] = useState({
        organization_id: JSON.parse(Cookies.get("userData"))?.organization_id,
        name: '',
        lastname: '',
        email: '',
        phone: '',
        RFC: ''
    });

    const [pagination, setPagination] = useState({
        limit: "10",
        skip: "0",
        organization: ''
    });

    const fetchClients = async () => {
        try {
            setLoading(true);
            const clientsData = await getAllClients(JSON.parse(Cookies.get("userData")).organization_id, pagination.limit, pagination.skip);
            setClients(clientsData);
        } catch (err) {
            setError(err.message || 'Failed to fetch clients');
        } finally {
            setLoading(false);
        }
    };

    const handleCreateClient = async (e) => {
        e.preventDefault();
        try {
            await createClient(newClient);
            setNewClient({ organization_id: JSON.parse(Cookies.get("userData"))?.organization_id, name: '', lastname: '', email: '', phone: '', RFC: '' });
            fetchClients();
        } catch (err) {
            setError(err.message || 'Failed to create client');
        }
    };

    const handleUpdateClient = async (e) => {
        e.preventDefault();
        try {
            const userData = JSON.parse(Cookies.get("userData"));
            await updateClient(
                editingClient._id, 
                editingClient, 
                userData.organization_id
            );
            setEditingClient(null);
            fetchClients();
        } catch (err) {
            setError(err.message || 'Failed to update client');
        }
    };
    
    const handleDeleteClient = async (clientId) => {
        try {
            await deleteClient(clientId);
            fetchClients();
        } catch (err) {
            setError(err.message || 'Failed to delete client');
        }
    };

    useEffect(() => {
        fetchClients();
    }, []);

    if (loading) return <div className="text-navy-blue-light">Loading clients...</div>;
    if (error) return <div className="text-red-600">Error: {error}</div>;

    return (
        <div className="clients-management p-6 bg-gray-100" style={{ backgroundColor: '#F4F6F9' }}>
            <h1 className="text-3xl font-serif font-bold mb-6 text-navy-blue-light border-b-2 border-gold pb-3">Client Management</h1>
            
            <form 
                onSubmit={editingClient ? handleUpdateClient : handleCreateClient} 
                className="bg-white p-6 rounded-lg shadow-md mb-6"
                style={{ 
                    backgroundColor: 'white', 
                    borderLeft: '4px solid #1A365D' 
                }}
            >
                <div className="grid grid-cols-2 gap-4">
                    <input 
                        type="text" 
                        placeholder="First Name" 
                        className="border-b border-navy-blue-light p-2 focus:outline-none focus:border-gold"
                        value={editingClient ? editingClient.name : newClient.name}
                        onChange={(e) => 
                            editingClient 
                                ? setEditingClient({...editingClient, name: e.target.value}) 
                                : setNewClient({...newClient, name: e.target.value})
                        }
                        required 
                    />
                    <input 
                        type="text" 
                        placeholder="Last Name" 
                        className="border-b border-navy-blue-light p-2 focus:outline-none focus:border-gold"
                        value={editingClient ? editingClient.lastname : newClient.lastname}
                        onChange={(e) => 
                            editingClient 
                                ? setEditingClient({...editingClient, lastname: e.target.value}) 
                                : setNewClient({...newClient, lastname: e.target.value})
                        }
                        required 
                    />
                    <input 
                        type="email" 
                        placeholder="Email" 
                        className="border-b border-navy-blue-light p-2 focus:outline-none focus:border-gold"
                        value={editingClient ? editingClient.email : newClient.email}
                        onChange={(e) => 
                            editingClient 
                                ? setEditingClient({...editingClient, email: e.target.value}) 
                                : setNewClient({...newClient, email: e.target.value})
                        }
                        required 
                    />
                    <input 
                        type="text" 
                        placeholder="Phone" 
                        className="border-b border-navy-blue-light p-2 focus:outline-none focus:border-gold"
                        value={editingClient ? editingClient.phone : newClient.phone}
                        onChange={(e) => 
                            editingClient 
                                ? setEditingClient({...editingClient, phone: e.target.value}) 
                                : setNewClient({...newClient, phone: e.target.value})
                        }
                    />
                    <input 
                        type="text" 
                        placeholder="RFC" 
                        className="border-b border-navy-blue-light p-2 focus:outline-none focus:border-gold"
                        value={editingClient ? editingClient.RFC : newClient.RFC}
                        onChange={(e) => 
                            editingClient 
                                ? setEditingClient({...editingClient, RFC: e.target.value}) 
                                : setNewClient({...newClient, RFC: e.target.value})
                        }
                    />
                </div>
                <div className="flex justify-between mt-4">
                    <button 
                        type="submit" 
                        className="px-6 py-2 rounded transition duration-300"
                        style={{
                            backgroundColor: '#1A365D',
                            color: 'white',
                            hover: { backgroundColor: '#D4AF37' }
                        }}
                    >
                        {editingClient ? 'Update Client' : 'Create Client'}
                    </button>
                    {editingClient && (
                        <button 
                            type="button" 
                            onClick={() => setEditingClient(null)}
                            className="px-6 py-2 rounded text-navy-blue-light border border-navy-blue-light hover:bg-gray-100"
                        >
                            Cancel
                        </button>
                    )}
                </div>
            </form>

            <div className="overflow-x-auto">
                <table className="w-full bg-white shadow-md rounded-lg overflow-hidden">
                    <thead className="bg-navy-blue-light text-white">
                        <tr>
                            <th className="p-3 text-left">Name</th>
                            <th className="p-3 text-left">Email</th>
                            <th className="p-3 text-left">Organization</th>
                            <th className="p-3 text-left">Phone</th>
                            <th className="p-3 text-left">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {clients.map(client => (
                            <tr key={client._id} className="border-b hover:bg-gray-50">
                                <td className="p-3">{client.name} {client.lastname}</td>
                                <td className="p-3">{client.email}</td>
                                <td className="p-3">{client.organization_id || 'N/A'}</td>
                                <td className="p-3">{client.phone || 'N/A'}</td>
                                <td className="p-3 space-x-2">
                                    <button 
                                        onClick={() => setEditingClient(client)} 
                                        className="px-3 py-1 rounded text-white"
                                        style={{ 
                                            backgroundColor: '#D4AF37',
                                            transition: 'background-color 0.3s'
                                        }}
                                    >
                                        Edit
                                    </button>
                                    <button 
                                        onClick={() => handleDeleteClient(client._id)} 
                                        className="px-3 py-1 rounded text-white bg-red-600 hover:bg-red-700"
                                    >
                                        Delete
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default ClientsManagement;